import type { ProtokollData, Summen } from '@wertermittlung/shared';

/** Typisierter API-Client. 401 → zurück zum Login. */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public felder?: { pfad: string; meldung: string }[],
  ) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: body !== undefined ? { 'content-type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 && !url.includes('/auth/')) {
    location.hash = '#/login';
  }
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string; felder?: never };
    throw new ApiError(res.status, data.error ?? `Fehler ${res.status}`, data.felder);
  }
  return (await res.json()) as T;
}

export interface User {
  id: string;
  name: string;
  email: string;
  rolle: 'verband' | 'vorstand' | 'wertermittler';
  vereinId: string | null;
}

export interface ProtokollListItem {
  id: string;
  status: 'entwurf' | 'abgeschlossen';
  summe: number;
  updatedAt: number;
  anlage: string;
  parzelle: string;
  paechter: string;
}

export interface ProtokollResponse {
  id: string;
  status: 'entwurf' | 'abgeschlossen';
  data: ProtokollData;
  faktor: number;
  jahr: number;
  summen: Summen;
  updatedAt: number;
}

export interface KatalogResponse {
  jahr: number;
  faktor: number;
  katalog: Record<
    'neben' | 'kulturen' | 'zier',
    { n: string; e: string; p: number; g: string | null }[]
  >;
  bauklassen: { k: number; label: string; rw: number }[];
  zustand: readonly { label: string; satz: number }[];
}

export const api = {
  login: (email: string, password: string) =>
    request<{ user: User }>('POST', '/api/auth/login', { email, password }),
  logout: () => request<{ ok: true }>('POST', '/api/auth/logout'),
  me: () => request<{ user: User }>('GET', '/api/auth/me'),

  kataloge: (jahr?: number) =>
    request<KatalogResponse>('GET', `/api/kataloge${jahr ? `?jahr=${jahr}` : ''}`),

  protokollListe: () => request<{ protokolle: ProtokollListItem[] }>('GET', '/api/protokolle'),
  protokollNeu: () => request<{ id: string }>('POST', '/api/protokolle'),
  protokoll: (id: string) => request<ProtokollResponse>('GET', `/api/protokolle/${id}`),
  protokollSpeichern: (id: string, data: ProtokollData) =>
    request<{ ok: true; summen: Summen; faktor: number; jahr: number }>(
      'PUT',
      `/api/protokolle/${id}`,
      { data },
    ),
  abschliessen: (id: string) => request<{ ok: true }>('POST', `/api/protokolle/${id}/abschliessen`),
  wiedereroeffnen: (id: string) =>
    request<{ ok: true }>('POST', `/api/protokolle/${id}/wiedereroeffnen`),

  fotoUrl: (protokollId: string, fotoId: string) =>
    `/api/protokolle/${protokollId}/fotos/${fotoId}`,
  fotoHochladen: async (protokollId: string, file: File): Promise<{ id: string }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`/api/protokolle/${protokollId}/fotos`, {
      method: 'POST',
      credentials: 'same-origin',
      body: form,
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, data.error ?? 'Upload fehlgeschlagen');
    }
    return (await res.json()) as { id: string };
  },
  fotoLoeschen: (protokollId: string, fotoId: string) =>
    request<{ ok: true }>('DELETE', `/api/protokolle/${protokollId}/fotos/${fotoId}`),
};
